import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { GoogleAddressSearch } from "../../../commons/GoogleAddressSearch.js";
import { Stack } from "../../../commons/layout/Stack.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationRegistrationDetailsBox_ViewerOrganisationFragment,
  useModifyOrganisationRegistrationDetailsMutation,
} from "./OrganisationRegistrationDetailsBox.generated.js";

interface Props {
  organisation: OrganisationRegistrationDetailsBox_ViewerOrganisationFragment;
}

export function OrganisationRegistrationDetailsBox({ organisation }: Props) {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate("OrganisationProfile");

  return (
    <SectionCard
      title={t("Company registration details", { ns: "OrganisationProfile" })}
      titleAdornment={
        <Tooltip
          title={t("Company registration details tooltip", {
            ns: "OrganisationProfile",
          })}
        >
          <IconButton color="secondary" aria-label="Show helper">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <RestrictedByOrganisationPermissionWithDebug
        permission="MANAGE_ORG"
        otherwise={
          <OrganisationRegistrationDetailsData organisation={organisation} />
        }
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <OrganisationRegistrationDetailsForm organisation={organisation} />
        ) : (
          <OrganisationRegistrationDetailsData organisation={organisation} />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}

function OrganisationRegistrationDetailsData({
  organisation,
}: {
  organisation: OrganisationRegistrationDetailsBox_ViewerOrganisationFragment;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <Stack flexDirection="column">
      <LabeledValue
        label={t("Full legal name", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.fullLegalName ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Tax number", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.taxNumber ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("VAT number", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.vatNumber ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Registration number", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.registrationNumber ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Register court", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.registerCourt ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Registration office", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.registrationOffice ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Managing director", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.managingDirector ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
    </Stack>
  );
}

interface FormValues {
  fullLegalName: string;
  taxNumber: string;
  vatNumber: string;
  registrationNumber: string;
  registerCourt: string;
  registrationOffice: string;
  managingDirector: string;
}

function OrganisationRegistrationDetailsForm({
  organisation,
}: {
  organisation: OrganisationRegistrationDetailsBox_ViewerOrganisationFragment;
}) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [modifyOrganisation] = useModifyOrganisationRegistrationDetailsMutation(
    {
      client,
    }
  );

  const initialValues = {
    fullLegalName: organisation.fullLegalName,
    taxNumber: organisation.taxNumber,
    vatNumber: organisation.vatNumber,
    registrationNumber: organisation.registrationNumber,
    registerCourt: organisation.registerCourt,
    registrationOffice: organisation.registrationOffice,
    managingDirector: organisation.managingDirector,
  };

  const validationSchema = Yup.object().shape({
    fullLegalName: Yup.string().label(
      t("Full legal name", { ns: "OrganisationProfile" })
    ),
    taxNumber: Yup.string().label(
      t("Tax number", { ns: "OrganisationProfile" })
    ),
    vatNumber: Yup.string().label(
      t("VAT number", { ns: "OrganisationProfile" })
    ),
    registrationNumber: Yup.string().label(
      t("Registration number", { ns: "OrganisationProfile" })
    ),
    registerCourt: Yup.string().label(
      t("Register court", { ns: "OrganisationProfile" })
    ),
    registrationOffice: Yup.string().label(
      t("Registration office", { ns: "OrganisationProfile" })
    ),
    managingDirector: Yup.string().label(
      t("Managing director", { ns: "OrganisationProfile" })
    ),
  });

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={async values => {
        try {
          await modifyOrganisation({
            variables: {
              input: {
                organisationId: organisation.id,
                fullLegalName: values.fullLegalName,
                taxNumber: values.taxNumber,
                vatNumber: values.vatNumber,
                registrationNumber: values.registrationNumber,
                registerCourt: values.registerCourt,
                registrationOffice: values.registrationOffice,
                managingDirector: values.managingDirector,
              },
            },
          });
        } catch (error) {
          if (error instanceof Error)
            enqueueSnackbar(error.message, { variant: "error" });
        }
      }}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <Form>
          <Stack flexDirection="column">
            <TextField
              disabled={false}
              name="fullLegalName"
              label={t("Full legal name", { ns: "OrganisationProfile" })}
            />
            <TextField
              disabled={false}
              name="taxNumber"
              label={t("Tax number", { ns: "OrganisationProfile" })}
            />
            <TextField
              disabled={false}
              name="vatNumber"
              label={t("VAT number", { ns: "OrganisationProfile" })}
            />
            <TextField
              disabled={false}
              name="registrationNumber"
              label={t("Registration number", { ns: "OrganisationProfile" })}
            />
            <TextField
              disabled={false}
              name="registerCourt"
              label={t("Register court", { ns: "OrganisationProfile" })}
            />
            <GoogleAddressSearch<never, never>
              label={t("Registration office", { ns: "OrganisationProfile" })}
              initialValue={formikProps.values.registrationOffice}
              onChange={value => {
                formikProps.setFieldValue(
                  "registrationOffice",
                  value?.text ?? ""
                );
              }}
              disabled={false}
            />
            <TextField
              disabled={false}
              name="managingDirector"
              label={t("Managing director", { ns: "OrganisationProfile" })}
            />
            <AutoSave />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
